import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, Sparkles, ArrowRight, History, TrendingUp, TrendingDown,
  DollarSign, AlertTriangle, PiggyBank, Cloud, Users, Box, Cpu,
  Shield, Lightbulb, Clock, Zap, MessageSquare, ChevronRight,
  Loader2, X, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for result types
const iconMap = {
  metric: DollarSign,
  breakdown: BarChart3,
  service_cost: BarChart3,
  anomalies: AlertTriangle,
  savings: PiggyBank,
  forecast: TrendingUp,
  comparison: TrendingUp,
  budget: Shield,
  recommendations: Lightbulb,
  kubernetes: Box,
  cloud_providers: Cloud,
  team_costs: Users,
  quick_action: Zap
};

const AdvancedSearch = () => {
  const { shouldShowData } = useDemoData();

  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchSuggestions('');
    // Focus input on load
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (query.length >= 2) {
        fetchSuggestions(query);
      }
    }, 300);
  if (!shouldShowData) {
    return <EmptyDataState title="Advanced Search" description="Connect your cloud account to see real data here." />;
  }


    return () => clearTimeout(debounce);
  }, [query]);

  const fetchSuggestions = async (q) => {
    try {
      const response = await axios.get(`${API_URL}/search/suggestions`, {
        params: { q }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/search/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setSearching(true);
    setShowSuggestions(false);
    setQuery(searchQuery);

    try {
      const response = await axios.post(`${API_URL}/search`, {
        query: searchQuery.trim()
      });
      setResults(response.data);
      fetchHistory();
    } catch (error) {
      toast.error('Search failed. Please try again.');
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleQuickQuery = async (queryType) => {
    setSearching(true);
    try {
      const response = await axios.post(`${API_URL}/search/quick-query`, null, {
        params: { query_type: queryType }
      });
      setResults(response.data);
      setQuery(response.data.query);
      fetchHistory();
    } catch (error) {
      toast.error('Quick query failed');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleResultClick = (result) => {
    if (result.action) {
      navigate(result.action);
    }
  };

  const clearResults = () => {
    setResults(null);
    setQuery('');
    inputRef.current?.focus();
  };

  const quickQueries = [
    { type: 'overview', label: 'Cost Overview', icon: DollarSign },
    { type: 'anomalies', label: 'Active Anomalies', icon: AlertTriangle },
    { type: 'savings', label: 'Savings Opportunities', icon: PiggyBank },
    { type: 'forecast', label: 'Cost Forecast', icon: TrendingUp },
    { type: 'teams', label: 'Team Costs', icon: Users },
    { type: 'kubernetes', label: 'K8s Costs', icon: Box }
  ];

  const renderResultCard = (result, index) => {
    const IconComponent = iconMap[result.type] || DollarSign;
    
    return (
      <Card 
        key={index} 
        className="cursor-pointer hover:shadow-md hover:border-emerald-500/30 transition-all"
        onClick={() => handleResultClick(result)}
        data-testid={`search-result-${index}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              result.type === 'anomalies' ? 'bg-amber-100 dark:bg-amber-900/30' :
              result.type === 'savings' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
              result.type === 'forecast' ? 'bg-emerald-100 dark:bg-blue-900/30' :
              'bg-slate-100 dark:bg-slate-800'
            }`}>
              <IconComponent className={`w-5 h-5 ${
                result.type === 'anomalies' ? 'text-amber-600' :
                result.type === 'savings' ? 'text-emerald-600' :
                result.type === 'forecast' ? 'text-emerald-600' :
                'text-slate-400 dark:text-slate-400'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                  {result.title}
                </h3>
                <Badge variant="outline" className="text-xs ml-2">
                  {(result.relevance * 100).toFixed(0)}% match
                </Badge>
              </div>
              <p className="text-sm text-slate-400 dark:text-slate-400 mb-3">
                {result.description}
              </p>
              
              {/* Render data preview based on type */}
              {result.data && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                  {renderDataPreview(result)}
                </div>
              )}
              
              {result.action && (
                <div className="flex items-center gap-1 text-emerald-600 text-sm mt-2">
                  View details <ChevronRight className="w-4 h-4" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDataPreview = (result) => {
    const { data, type } = result;
    
    switch (type) {
      case 'metric':
        return (
          <div className="flex items-center gap-4">
            <div className="text-2xl font-bold text-slate-900 dark:text-white">
              ${data.value?.toLocaleString()}
            </div>
            {data.change !== undefined && (
              <div className={`flex items-center gap-1 text-sm ${
                data.change >= 0 ? 'text-rose-500' : 'text-emerald-500'
              }`}>
                {data.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {data.change >= 0 ? '+' : ''}{data.change}%
              </div>
            )}
          </div>
        );

      case 'anomalies':
        return (
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-rose-100 text-rose-700">{data.critical} Critical</Badge>
            <Badge className="bg-amber-100 text-amber-700">{data.count - data.critical} Other</Badge>
          </div>
        );

      case 'savings':
        return (
          <div className="space-y-2">
            <div className="text-xl font-bold text-emerald-600">
              ${data.total_savings?.toLocaleString()} potential savings
            </div>
            <div className="text-sm text-slate-400">
              {data.recommendations_count} recommendations available
            </div>
          </div>
        );

      case 'forecast':
        return (
          <div className="space-y-2">
            <div className="text-xl font-bold text-slate-900 dark:text-white">
              ${data.predicted?.toLocaleString()}
            </div>
            <div className="text-sm text-slate-400">
              Range: ${data.lower_bound?.toLocaleString()} - ${data.upper_bound?.toLocaleString()}
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">
              {(data.confidence * 100).toFixed(0)}% confidence
            </Badge>
          </div>
        );

      case 'budget':
        return (
          <div className="space-y-2">
            {data.budgets?.slice(0, 2).map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${
                  b.status === 'warning' ? 'bg-amber-500' : 
                  b.status === 'exceeded' ? 'bg-rose-500' : 'bg-emerald-500'
                }`} />
                <span className="text-sm">{b.name}</span>
                <span className="text-sm text-slate-400">{b.percent.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        );

      case 'cloud_providers':
        return (
          <div className="flex items-center gap-4">
            {data.providers?.map((p, i) => (
              <div key={i} className="text-center">
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="text-xs text-slate-400">{p.percent}%</div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="advanced-search-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center justify-center gap-3 mb-2">
          <Sparkles className="w-8 h-8 text-emerald-500" />
          AI-Powered Cost Search
        </h1>
        <p className="text-slate-400 dark:text-slate-400">
          Ask questions about your cloud costs in natural language
        </p>
      </div>

      {/* Search Input */}
      <div className="max-w-2xl mx-auto relative">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Try: 'Show me cost anomalies' or 'What are my top saving opportunities?'"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            className="pl-12 pr-24 py-6 text-lg rounded-xl border-2 focus:border-emerald-500"
            data-testid="search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {query && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearResults}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            <Button
              onClick={() => handleSearch()}
              disabled={searching || !query.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 h-10"
              data-testid="search-btn"
            >
              {searching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && !results && (
          <Card className="absolute w-full mt-2 z-50 shadow-lg">
            <CardContent className="p-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="w-full text-left px-4 py-2 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-sm transition-colors"
                  onClick={() => handleSearch(suggestion)}
                  data-testid={`suggestion-${index}`}
                >
                  <Search className="w-4 h-4 inline mr-2 text-slate-400" />
                  {suggestion}
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Queries */}
      {!results && (
        <div className="max-w-3xl mx-auto">
          <h3 className="text-sm font-medium text-slate-400 mb-3 text-center">Quick Queries</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {quickQueries.map((qq) => (
              <Button
                key={qq.type}
                variant="outline"
                onClick={() => handleQuickQuery(qq.type)}
                className="gap-2"
                disabled={searching}
                data-testid={`quick-query-${qq.type}`}
              >
                <qq.icon className="w-4 h-4" />
                {qq.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="max-w-3xl mx-auto space-y-6">
          {/* AI Summary */}
          <Card className="bg-emerald-50 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-1">
                    AI Analysis
                  </h3>
                  <p className="text-emerald-800 dark:text-emerald-200 text-sm">
                    {results.ai_summary}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Intent Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Detected intent:</span>
            <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {results.intent?.replace(/_/g, ' ')}
            </Badge>
          </div>

          {/* Results Grid */}
          <div className="space-y-4">
            {results.results?.map((result, index) => renderResultCard(result, index))}
          </div>

          {/* Follow-up Suggestions */}
          {results.suggestions && results.suggestions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Related Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {results.suggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSearch(suggestion)}
                      className="text-sm"
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Back to Search */}
          <div className="text-center">
            <Button variant="outline" onClick={clearResults} className="gap-2">
              <Search className="w-4 h-4" />
              New Search
            </Button>
          </div>
        </div>
      )}

      {/* Recent History */}
      {!results && history.length > 0 && (
        <div className="max-w-2xl mx-auto">
          <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <History className="w-4 h-4" />
            Recent Searches
          </h3>
          <div className="space-y-1">
            {history.slice(0, 5).map((item, index) => (
              <button
                key={index}
                className="w-full text-left px-4 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-sm transition-colors flex items-center gap-2"
                onClick={() => handleSearch(item.query)}
              >
                <History className="w-4 h-4 text-slate-400" />
                <span className="flex-1 truncate">{item.query}</span>
                <Badge variant="outline" className="text-xs">{item.intent?.replace(/_/g, ' ')}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      {!results && (
        <Card className="max-w-2xl mx-auto bg-slate-50 dark:bg-slate-800/50 border-dashed">
          <CardContent className="p-4">
            <h4 className="font-medium text-slate-900 dark:text-white mb-2">Search Tips</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Ask about costs: "What's my total spend this month?"</li>
              <li>• Find anomalies: "Show me unusual spending patterns"</li>
              <li>• Get savings: "How can I reduce my AWS or Snowflake costs?"</li>
              <li>• Compare: "Compare this month vs last month"</li>
              <li>• Filter by team: "What is the Platform team spending?"</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedSearch;
